import { AsyncPipe, NgIf } from '@angular/common';
import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ConfigService } from '@core/services/config.service';
import { ThemeService } from '@core/services/theme.service';
import {
  PAGE_TITLES,
  usePageTitleWithLangChange,
} from '@core/utils/page-title.util';
import { PagePreviewBaseComponent } from '@feature/preview-management/components/page-preview-base/page-preview-base.component';
import { ValidatorComponent } from '@feature/survey-management/components/validator/validator.component';
import { DialogService } from '@ngneat/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ValidatorType } from '@shared/models/enums/validator-type.enum';
import { ErrorState } from '@shared/state/error.state';
import { catchError, Observable } from 'rxjs';
import { ChooseLanguageComponent } from '../../../../core/components/choose-language/choose-language.component';
import { NavigationComponent } from '../../../../core/components/navigation/navigation.component';
import { CustomTranslateLoader } from '../../../../core/i18n/custom-translate-loader';
import { AnswerModuleService } from '../../../../core/services/answer-module.service';
import { RedirectService } from '../../../../core/services/redirect.service';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { LayoutComponent } from '../../../../shared/components/layout/layout.component';
import { FocusTrapDirective } from '../../../../shared/directives/focus-trap.directive';
import { LanguageSlotDirective } from '../../../../shared/directives/language-slot.directive';
import { AnswerModuleResponse } from '../../../../shared/models/answer-module-response';
import { ChooseLanguage } from '../../../../shared/models/choose-language';
import { EventChooseLanguageComponent } from '../../../../shared/models/components/event-choose-language';
import { GdprRequest } from '../../../../shared/models/gdpr-request';
import { GDPRStatement } from '../../../../shared/models/gdpr-statements';
import { GDPRView } from '../../../../shared/models/views-models/gdpr-view';
import { NewlineToBrPipe } from '../../../../shared/pipes/new-line-to-br.pipe';
import { SafeHtmlPipe } from '../../../../shared/pipes/safe-html.pipe';
import {
  createLanguageObject,
  mapLanguageOptions,
  updateTextDirection,
} from '../../../../shared/utils/helper.util';
import {
  cleanupViewportHeight,
  setupViewportHeight,
} from '../../../../shared/utils/viewport.util';
import { GdprStatementComponent } from '../gdpr-statement/gdpr-statement.component';

@Component({
  selector: 'app-gdpr-page',
  imports: [
    NavigationComponent,
    LayoutComponent,
    LanguageSlotDirective,
    FormsModule,
    TranslateModule,
    ButtonComponent,
    GdprStatementComponent,
    ChooseLanguageComponent,
    SafeHtmlPipe,
    NewlineToBrPipe,
    AsyncPipe,
    ValidatorComponent,
    NgIf,
    FocusTrapDirective,
  ],
  templateUrl: './gdpr-page.component.html',
  styleUrl: './gdpr-page.component.scss',
})
export class GdprPageComponent
  extends PagePreviewBaseComponent
  implements OnInit, OnDestroy
{
  private setPageTitle = usePageTitleWithLangChange();
  constructor(
    private route: ActivatedRoute,
    protected override redirectService: RedirectService,
    private answerService: AnswerModuleService,
    private translateService: TranslateService,
    private translateLoader: CustomTranslateLoader,
    protected override themeService: ThemeService,
    private configService: ConfigService,
  ) {
    super(redirectService, themeService);
    this.useClientValidation = this.configService.get('USE_CLIENT_VALIDATION');
  }

  @HostListener('window:popstate', ['$event'])
  onPopState() {
    history.pushState(null, '');
  }

  useClientValidation: boolean;
  gdprView?: GDPRView;
  statement?: GDPRStatement;
  gdprConsent: boolean = false;
  chooseLanguageComponent?: EventChooseLanguageComponent;
  gdprPageTranslationkeys: string[] = ['navigation.', 'gdpr.', 'validation.'];
  languageOptions: { value: string; label: string; src: string }[] = [];
  selectedLanguage: string = '';
  validator = ValidatorType;
  private readonly destroyRef = inject(DestroyRef);
  private beforeUnloadHandler = this.onBeforeUnload.bind(this);
  store = inject(Store);
  error$ = this.store.select(ErrorState.getErrors);
  protected override onResponseSet(value: AnswerModuleResponse): void {
    if (this.previewMode && value?.view?.type === 'GDPR_CONSENT') {
      this.gdprView = value.view;
      this.selectedLanguage = value.language;
      this.statement = value.view.data.statement;
      this.initLanguage();
    }
  }

  onBeforeUnload(event: Event): void {
    event.preventDefault();
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    cleanupViewportHeight();
  }

  private dialog = inject(DialogService);
  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    return this.allowNavigation;
  }

  openGdprStatements(tpl: TemplateRef<any>) {
    this.dialog.open(tpl, {
      id: 'id',
      closeButton: false,
      size: 'lg',
    });
  }

  openGdprDecline(tpl: TemplateRef<any>) {
    this.dialog.open(tpl, {
      id: 'id',
      closeButton: false,
      size: 'md',
    });
  }

  ngOnInit(): void {
    this.setPageTitle(PAGE_TITLES.GDPR.key, PAGE_TITLES.GDPR.fallback);

    // Set viewport height for mobile browsers
    setupViewportHeight();

    if (!this.previewMode) {
      window.addEventListener('beforeunload', this.beforeUnloadHandler);
    } else {
      this.gdprPageTranslationkeys.push('preview.');
    }
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const response = history.state.data as AnswerModuleResponse;
        if (response && response.view.type === 'GDPR_CONSENT') {
          this.response = response;
          this.gdprView = response.view;
          this.statement = response.view.data.statement;
        }
        this.initLanguage();
      });

    if (this.response.language) {
      updateTextDirection(this.response.language);
    }
  }

  initLanguage() {
    this.chooseLanguageComponent = this.gdprView?.data.components.find(
      (i) => i.$type === '@event-choose-language',
    );
    if (this.chooseLanguageComponent) {
      this.languageOptions = mapLanguageOptions(this.chooseLanguageComponent);
    } else {
      this.selectedLanguage = this.response.language;
      this.translateLoader
        .getTranslation(
          this.selectedLanguage,
          this.gdprPageTranslationkeys,
          this.response.fallbackLanguage,
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((translation) => {
          this.translateService.setTranslation(
            this.selectedLanguage,
            translation,
            true,
          );
          this.translateService.use(this.selectedLanguage);
        });
    }
  }

  setLanguage({ language, calledWithInitialLanguage }: ChooseLanguage) {
    this.selectedLanguage = language;

    updateTextDirection(language);

    if (!calledWithInitialLanguage) {
      this.postLanguage();
    }
  }

  postLanguage() {
    if (this.response?.responseId && this.chooseLanguageComponent) {
      const languageRequest = createLanguageObject(
        this.response?.responseId,
        this.selectedLanguage,
      );
      this.answerService
        .postLanguage(
          this.response.responseId,
          this.response.stateId,
          languageRequest,
        )
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError(() => {
            this.allowNavigation = true;
            return [];
          }),
        )
        .subscribe((res) => {
          if (res.body?.view.type === 'GDPR_CONSENT') {
            this.response = res.body;
            this.statement = res.body.view.data.statement;
          }
        });
    }
  }

  declineGdpr() {
    this.gdprConsent = false;
    this.next();
  }

  next() {
    if (this.response?.responseId) {
      const gdprRequest: GdprRequest = {
        type: 'GDPR_CONSENT',
        data: {
          $type: 'gdprConsentResponseData',
          consent: this.gdprConsent,
          revisionId: this.statement?.revisionId,
          statementId: this.statement?.statementId,
        },
        responseId: this.response.responseId,
      };

      const body: GdprRequest[] =
        this.configService.get('USE_CLIENT_VALIDATION') || this.gdprConsent
          ? [gdprRequest]
          : [];

      this.answerService
        .getNextState(this.response.responseId, this.response.stateId, body)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError(() => {
            this.allowNavigation = true;
            return [];
          }),
        )
        .subscribe((response) => {
          if (response.body) {
            this.handlePreviewMode(response.body);
          }
        });
    }
  }

  hasConsentError(): boolean {
    const errors = this.store.selectSnapshot(ErrorState.getErrors);
    return (
      errors?.some(
        (e) => e.translationKey === 'validation.general.missingconsentanswer',
      ) ?? false
    );
  }

  getDuration(): string {
    const duration = this.statement?.responseRetentionDuration;

    if (duration && duration < 0) {
      return this.translateService.instant('gdpr.statement.retention.unlimited');
    }

    if (!duration || duration <= 0) return '';
    return `${duration}`;
  }

  getAfterText(): string {
    const interval = this.statement?.responseRetentionDurationInterval;
    const unit = this.statement?.responseRetentionDuration;
    if (!interval || !unit) return '';

    if (unit < 0) {
      return '';
    }

    const key =
      unit > 1
        ? `gdpr.statement.retention.${interval.toLowerCase()}s`
        : `gdpr.statement.retention.${interval.toLowerCase()}`;

    return key;
  }
}
